import {
  uuidv7,
  type DocumentDeclaration,
  type DocumentTemplate,
  type DocumentTemplateContent,
  type ModuleManifest,
} from '@nexus/core';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';
import { moduleSettingsPath } from '../modules/settings';
import { canIn } from '../session/session';

/** Kernel record of the templates, owner of their pictures (`core.files`). */
export const DOCUMENT_TEMPLATES = 'core.document-templates';
/** Pictures in templates; the server allows images of up to 5 MB. */
export const TEMPLATE_IMAGE_BYTES = 5 * 1024 * 1024;
export const TEMPLATE_IMAGE_ACCEPT = 'image/png,image/jpeg';

export interface DocumentTemplateList {
  items: DocumentTemplate[];
  /** Default templates of the module whose names the company does not have yet. */
  missingDefaults: number;
}

/** A document a module declares, with the module that owns it. */
export interface DeclaredDocument {
  owner: ModuleManifest;
  declaration: DocumentDeclaration;
}

const base = (key: string) => `/documents/${encodeURIComponent(key)}/templates`;

export const documentTemplatesQuery = (key: string) =>
  queryOptions({
    queryKey: ['documents', 'templates', key],
    queryFn: ({ signal }) => api.get<DocumentTemplateList>(base(key), { signal }),
  });

export interface TemplateChanges {
  name?: string;
  isDefault?: boolean;
  active?: boolean;
  content?: DocumentTemplateContent;
  version: number;
}

/** Changes to templates; the server checks `<module>.settings.manage` on every call. */
export const documentTemplateApi = {
  create: (
    key: string,
    input: { name: string; content: DocumentTemplateContent; isDefault?: boolean },
  ) => api.post<DocumentTemplate>(base(key), input),
  update: (key: string, id: string, changes: TemplateChanges) =>
    api.patch<DocumentTemplate>(`${base(key)}/${id}`, changes),
  copy: (key: string, id: string, name: string) =>
    api.post<DocumentTemplate>(`${base(key)}/${id}/copy`, { name }),
  remove: (key: string, id: string) => api.delete(`${base(key)}/${id}`),
  reorder: (key: string, ids: readonly string[]) =>
    api.put<{ items: DocumentTemplate[] }>(`${base(key)}/order`, { ids }),
  loadDefaults: (key: string) => api.post<{ added: number }>(`${base(key)}/defaults`),
};

/** Error codes of the templates API with their own text. */
const TEMPLATE_ERRORS = [
  'name-taken',
  'default-inactive',
  'block-not-allowed',
  'source-unknown',
  'image-unknown',
  'template-too-large',
  'content-invalid',
] as const;

/** Texts for `useAdminError`, by error code. */
export function templateErrors(t: (key: string) => string): Record<string, string> {
  return Object.fromEntries(
    TEMPLATE_ERRORS.map((code) => [code, t(`documents.templates.errors.${code}`)]),
  );
}

/** Stores a picture of a template, e.g. a logo, and returns its file ID. */
export async function uploadTemplateImage(
  templateId: string,
  file: Blob,
  name: string,
): Promise<string> {
  const id = uuidv7();
  const query = new URLSearchParams({ entity: DOCUMENT_TEMPLATES, recordId: templateId });
  await api.put(`/files/${id}?${query.toString()}`, file, {
    headers: { 'X-File-Name': encodeURIComponent(name) },
  });
  return id;
}

/** A template without content, as „Neue Vorlage“ creates it. */
export function emptyTemplateContent(): DocumentTemplateContent {
  return {
    orientation: 'portrait',
    cover: null,
    coverAlone: false,
    header: null,
    footer: null,
    blocks: [],
  };
}

/** Documents of active modules (and their active parents). */
function declaredDocuments(
  manifests: readonly ModuleManifest[],
  session: SessionInfo,
): DeclaredDocument[] {
  const active = new Set(session.modules);
  return manifests
    .filter((m) => active.has(m.id) && (!m.parent || active.has(m.parent)))
    .flatMap((owner) => (owner.documents ?? []).map((declaration) => ({ owner, declaration })));
}

/**
 * Documents of a module and its submodules whose templates the person may change
 * (`<module>.settings.manage`), for the module's settings page.
 */
export function documentsOfModule(
  module: ModuleManifest,
  manifests: readonly ModuleManifest[],
  session: SessionInfo,
): DeclaredDocument[] {
  return declaredDocuments(manifests, session).filter(
    ({ owner }) =>
      (owner.id === module.id || owner.parent === module.id) &&
      canIn(session, `${owner.id}.settings.manage`),
  );
}

/** Where the editor of a template opens: below the settings page of the main module. */
export function templateEditorPath(moduleId: string, templateId: string): string {
  return `${moduleSettingsPath(moduleId)}/documents/${templateId}`;
}
